import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, ChevronRight, CheckCircle2, Clock, ShieldCheck,
  Sparkles, ChevronDown, Info, MessageCircle, FileText,
  AlertTriangle, CalendarRange, Coins, MapPin, Building2,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import RequestAppointmentForm from '../components/RequestAppointmentForm';
import BeforeAfterGallery from '../components/BeforeAfterGallery';
import { TREATMENTS, getCategoryBySlug } from '../data/treatments';
import { useCatalog } from '../context/CatalogContext';
import { buildTreatmentSEO, buildTreatmentJsonLd, buildFaqJsonLd, buildBreadcrumbJsonLd } from '../lib/seo';
import { useContactInfo, getWhatsAppHref } from '../contexts/ContactInfoContext';

const TreatmentDetailPage = () => {
  const { slug } = useParams();
  const { getTreatmentBySlug, getCategoryBySlug: catalogGetCategory } = useCatalog();
  const treatment = getTreatmentBySlug(slug);
  const { contactInfo } = useContactInfo();

  if (!treatment) {
    return <Navigate to="/treatments" replace />;
  }

  const accent = treatment.accent || { from: '#1FA7A1', to: '#0F8783' };
  const category = catalogGetCategory(treatment.category) || getCategoryBySlug(treatment.category);
  const hasFullDetail = Boolean(treatment.whatIs);
  const relatedTreatments = (treatment.related || [])
    .map(getTreatmentBySlug)
    .filter(Boolean);

  const seo = buildTreatmentSEO(treatment);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const procedureJsonLd = buildTreatmentJsonLd(treatment, origin);
  const faqJsonLd = hasFullDetail ? buildFaqJsonLd(treatment.faq) : null;
  const breadcrumbJsonLd = buildBreadcrumbJsonLd(
    [
      { name: 'Home', url: '/' },
      { name: 'Treatments', url: '/treatments' },
      ...(category ? [{ name: category.name, url: `/treatments?category=${treatment.category}` }] : []),
      { name: treatment.name, url: `/treatments/${treatment.slug}` },
    ],
    origin || 'https://vietclinics.com',
  );

  const whatsappHref = getWhatsAppHref(
    contactInfo,
    `Hi VietClinics, I'd like guidance on ${treatment.name} in Vietnam.`,
  );

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={`${origin}/treatments/${treatment.slug}`} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:image" content={treatment.image} />
        <meta property="og:type" content="article" />
        {procedureJsonLd && (
          <script type="application/ld+json">{JSON.stringify(procedureJsonLd)}</script>
        )}
        {faqJsonLd && (
          <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        )}
        {breadcrumbJsonLd && (
          <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={treatment.image} />
      </Helmet>
      <Header />

      {/* Breadcrumbs */}
      <div className="bg-white border-b border-slate-100">
        <div className="container mx-auto px-4 max-w-6xl py-3">
          <nav className="flex items-center gap-1.5 text-xs text-slate-500">
            <Link to="/" className="hover:text-[#1FA7A1]">Home</Link>
            <ChevronRight className="h-3 w-3 text-slate-300" />
            <Link to="/treatments" className="hover:text-[#1FA7A1]">Treatments</Link>
            <ChevronRight className="h-3 w-3 text-slate-300" />
            <span className="text-slate-700 font-medium">{treatment.name}</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${accent.from} 0%, ${accent.to} 100%)` }}
        data-testid={`treatment-hero-${treatment.slug}`}
      >
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, white 0%, transparent 50%), radial-gradient(circle at 10% 80%, white 0%, transparent 45%)' }}
        />
        <div className="container mx-auto px-4 max-w-6xl py-16 lg:py-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 text-white">
              <span className="inline-flex items-center bg-white/15 backdrop-blur-sm border border-white/20 text-white/90 text-[11px] font-bold px-3 py-1.5 rounded-full uppercase tracking-widest">
                {category?.name || treatment.category}
              </span>
              <h1 className="font-extrabold tracking-tight leading-[1.05] mt-5"
                style={{ fontSize: 'clamp(2.4rem, 4.6vw, 3.8rem)' }}
              >
                {treatment.name} in Vietnam
              </h1>
              <p className="text-white/85 text-lg mt-5 leading-relaxed max-w-xl">
                {treatment.shortDesc}
              </p>

              <div className="flex flex-wrap gap-3 mt-6">
                <div className="inline-flex items-center gap-2 bg-white/12 backdrop-blur-sm border border-white/15 rounded-full px-4 py-2">
                  <span className="text-xs font-semibold text-white/70">Starting Price</span>
                  <span className="text-sm font-bold text-white">{treatment.startingPrice}</span>
                </div>
                <div className="inline-flex items-center gap-2 bg-white/12 backdrop-blur-sm border border-white/15 rounded-full px-4 py-2">
                  <Clock className="h-3.5 w-3.5 text-white/70" />
                  <span className="text-sm font-bold text-white">{treatment.recoveryTime}</span>
                </div>
                <div className="inline-flex items-center gap-2 bg-white/12 backdrop-blur-sm border border-white/15 rounded-full px-4 py-2">
                  <ShieldCheck className="h-3.5 w-3.5 text-white/70" />
                  <span className="text-sm font-bold text-white">Verified Providers</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mt-8">
                <a
                  href="#request-form"
                  className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold rounded-full px-7 py-3.5 transition-all hover:-translate-y-0.5"
                  style={{ boxShadow: '0 12px 28px rgba(0,0,0,0.18)' }}
                  data-testid="treatment-hero-cta"
                >
                  Request Appointment <ArrowRight className="h-4 w-4" />
                </a>
                <Link
                  to="/treatments"
                  className="inline-flex items-center gap-2 text-white font-semibold rounded-full px-6 py-3.5 border border-white/30 hover:bg-white/15 transition-all backdrop-blur-sm"
                >
                  Explore Treatments
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative">
                <img
                  src={treatment.image}
                  alt={treatment.name}
                  className="w-full h-[420px] object-cover rounded-[28px]"
                  style={{ boxShadow: '0 30px 60px rgba(0,0,0,0.25)' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is + Who for */}
      {hasFullDetail && (
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <div className="lg:col-span-3">
              <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">About the Treatment</p>
              <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
                What is {treatment.name}?
              </h2>
              <p className="text-slate-600 mt-4 text-base leading-relaxed whitespace-pre-line">
                {treatment.whatIs}
              </p>
            </div>
            <div className="lg:col-span-2">
              <div className="bg-slate-50 rounded-[24px] p-6 border border-slate-100">
                <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">Who Is It For</p>
                <ul className="space-y-2.5">
                  {treatment.whoFor.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-slate-700">
                      <CheckCircle2 className="h-4 w-4 text-[#1FA7A1] flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* "Personalised guide on request" — for minimal entries */}
      {!hasFullDetail && (
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <div className="bg-slate-50 rounded-[24px] p-7 sm:p-10 border border-slate-100 text-center">
              <div
                className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-4"
                style={{ background: `${accent.from}15` }}
              >
                <FileText className="h-7 w-7" style={{ color: accent.from }} />
              </div>
              <p className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: accent.from }}>
                Personalised guide on request
              </p>
              <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                Tailored {treatment.name} treatment plan
              </h2>
              <p className="text-slate-600 mt-3 text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
                Every {treatment.name.toLowerCase()} plan is different. Submit a request and our concierge team will share a personalised guide — including suitable providers, indicative pricing, and recovery expectations — within hours.
              </p>
              <a
                href="#request-form"
                className="mt-6 inline-flex items-center gap-2 bg-[#1FA7A1] hover:bg-[#178f8a] text-white font-bold rounded-full px-7 py-3.5 transition-all hover:-translate-y-0.5"
                data-testid="minimal-request-cta"
              >
                Get Personalized Quote <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Full-detail sections (flagship treatments only) */}
      {hasFullDetail && (
        <>
      {/* Benefits */}
      <section className="py-16" style={{ background: '#F8FAFC' }}>
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-10">
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">Why Patients Choose Vietnam</p>
            <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
              Key Benefits
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {treatment.benefits.map((b) => (
              <div
                key={b.title}
                className="bg-white rounded-[20px] border border-slate-100 p-6"
                style={{ boxShadow: '0 4px 16px rgba(15,42,85,0.04)' }}
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: `${accent.from}15` }}
                >
                  <Sparkles className="h-5 w-5" style={{ color: accent.from }} />
                </div>
                <h3 className="font-extrabold text-slate-900 text-base mb-1.5">{b.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Risks & Important Considerations */}
      {treatment.risks && treatment.risks.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="text-center mb-10">
              <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">Important to Know</p>
              <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
                Risks & Considerations
              </h2>
              <p className="text-slate-500 text-sm mt-3 max-w-xl mx-auto">
                Balanced, educational information so you can plan with confidence.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {treatment.risks.map((r) => (
                <div
                  key={r.title}
                  className="bg-slate-50 rounded-[20px] border border-slate-100 p-6"
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                    style={{ background: `${accent.from}12` }}
                  >
                    <AlertTriangle className="h-5 w-5" style={{ color: accent.from }} />
                  </div>
                  <h3 className="font-extrabold text-slate-900 text-base mb-1.5">{r.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{r.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Procedure */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-10">
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">Step by Step</p>
            <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
              How the Procedure Works
            </h2>
          </div>
          <div className="space-y-4">
            {treatment.procedure.map((p) => (
              <div
                key={p.step}
                className="flex gap-5 bg-slate-50 rounded-[20px] p-5 border border-slate-100"
              >
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center font-extrabold text-white text-lg flex-shrink-0"
                  style={{ background: `linear-gradient(135deg, ${accent.from}, ${accent.to})` }}
                >
                  {p.step}
                </div>
                <div>
                  <h3 className="font-extrabold text-slate-900 text-base">{p.title}</h3>
                  <p className="text-slate-600 text-sm mt-1 leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recovery + Price Guide */}
      <section className="py-16" style={{ background: '#F8FAFC' }}>
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-2 bg-white rounded-[24px] p-7 border border-slate-100" style={{ boxShadow: '0 4px 16px rgba(15,42,85,0.04)' }}>
              <div className="flex items-center gap-2 mb-3">
                <Clock className="h-5 w-5 text-[#1FA7A1]" />
                <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest">Recovery</p>
              </div>
              <h3 className="text-xl font-extrabold text-slate-900 tracking-tight mb-3">
                What to expect
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">{treatment.recovery}</p>
            </div>

            <div className="lg:col-span-3 bg-white rounded-[24px] p-7 border border-slate-100" style={{ boxShadow: '0 4px 16px rgba(15,42,85,0.04)' }}>
              <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">Price Guide in Vietnam</p>
              <h3 className="text-xl font-extrabold text-slate-900 tracking-tight mb-5">
                Indicative pricing
              </h3>
              <ul className="space-y-3">
                {treatment.priceGuide.map((p) => (
                  <li key={p.option} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
                    <span className="text-sm text-slate-700">{p.option}</span>
                    <span className="text-sm font-bold text-slate-900">{p.price}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-400 mt-4 flex items-start gap-1.5">
                <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                Pricing varies by clinic and complexity. Submit a request for a personalised, no-obligation quote.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Typical Stay in Vietnam */}
      {treatment.stayInVietnam && treatment.stayInVietnam.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="text-center mb-10">
              <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">Plan Your Trip</p>
              <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
                Typical Stay in Vietnam
              </h2>
              <p className="text-slate-500 text-sm mt-3 max-w-xl mx-auto">
                A guide to how international patients usually structure each stage of the journey.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {treatment.stayInVietnam.map((s) => (
                <div
                  key={s.stage}
                  className="bg-slate-50 rounded-[20px] border border-slate-100 p-6"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ background: `${accent.from}12` }}
                    >
                      <CalendarRange className="h-5 w-5" style={{ color: accent.from }} />
                    </div>
                    <span
                      className="text-[11px] font-bold uppercase tracking-widest"
                      style={{ color: accent.from }}
                    >
                      {s.duration}
                    </span>
                  </div>
                  <h3 className="font-extrabold text-slate-900 text-base mb-1.5">{s.stage}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing Factors */}
      {treatment.pricingFactors && treatment.pricingFactors.length > 0 && (
        <section className="py-16" style={{ background: '#F8FAFC' }}>
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="text-center mb-10">
              <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">What Shapes the Quote</p>
              <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
                Pricing Factors
              </h2>
              <p className="text-slate-500 text-sm mt-3 max-w-xl mx-auto">
                Indicative ranges depend on the details of your case. These are the main factors that influence the final, personalised quote.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {treatment.pricingFactors.map((f) => (
                <div
                  key={f.factor}
                  className="bg-white rounded-[20px] border border-slate-100 p-6"
                  style={{ boxShadow: '0 4px 16px rgba(15,42,85,0.04)' }}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${accent.from}12` }}
                    >
                      <Coins className="h-4 w-4" style={{ color: accent.from }} />
                    </div>
                    <div>
                      <h3 className="font-extrabold text-slate-900 text-base mb-1">{f.factor}</h3>
                      <p className="text-slate-600 text-sm leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-6 flex items-start gap-1.5 max-w-2xl mx-auto">
              <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              All figures shown on this page are estimated treatment ranges only. Final pricing is confirmed in writing after a personalised consultation with a clinic partner.
            </p>
          </div>
        </section>
      )}

      {/* Why Vietnam */}
      {treatment.whyVietnam && treatment.whyVietnam.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center mb-10">
              <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">International Patient Perspective</p>
              <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
                Why Patients Choose Vietnam
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {treatment.whyVietnam.map((w) => (
                <div
                  key={w.title}
                  className="bg-slate-50 rounded-[20px] border border-slate-100 p-6"
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                    style={{ background: `${accent.from}12` }}
                  >
                    <MapPin className="h-5 w-5" style={{ color: accent.from }} />
                  </div>
                  <h3 className="font-extrabold text-slate-900 text-base mb-1.5">{w.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{w.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why VietClinics */}
      {treatment.whyVietClinics && treatment.whyVietClinics.length > 0 && (
        <section className="py-16" style={{ background: '#F8FAFC' }}>
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center mb-10">
              <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">Concierge Support</p>
              <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
                Why Use VietClinics
              </h2>
              <p className="text-slate-500 text-sm mt-3 max-w-xl mx-auto">
                VietClinics is not a hospital or clinic. We are an independent healthcare coordination platform that supports international patients across Vietnam.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {treatment.whyVietClinics.map((w) => (
                <div
                  key={w.title}
                  className="bg-white rounded-[20px] border border-slate-100 p-6"
                  style={{ boxShadow: '0 4px 16px rgba(15,42,85,0.04)' }}
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                    style={{ background: `${accent.from}12` }}
                  >
                    <Building2 className="h-5 w-5" style={{ color: accent.from }} />
                  </div>
                  <h3 className="font-extrabold text-slate-900 text-base mb-1.5">{w.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{w.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-10">
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">Common Questions</p>
            <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
              Frequently Asked
            </h2>
          </div>
          <div className="space-y-3">
            {treatment.faq.map((item, i) => (
              <FaqItem key={i} index={i} item={item} />
            ))}
          </div>
        </div>
      </section>
        </>
      )}

      {/* Before & After Gallery — flagship only */}
      {hasFullDetail && (
        <BeforeAfterGallery treatmentName={treatment.name} accent={accent} />
      )}

      {/* Request Form */}
      <section id="request-form" className="py-16" style={{ background: '#F8FAFC' }}>
        <div className="container mx-auto px-4 max-w-4xl">
          {whatsappHref && (
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="treatment-whatsapp-cta"
              className="block mb-6 bg-white border border-slate-100 rounded-[20px] p-5 sm:p-6 hover:-translate-y-0.5 transition-all"
              style={{ boxShadow: '0 4px 16px rgba(15,42,85,0.04)' }}
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ background: '#25D36615' }}
                >
                  <MessageCircle className="h-6 w-6" style={{ color: '#25D366' }} />
                </div>
                <div className="flex-1">
                  <p className="text-[11px] font-bold uppercase tracking-widest mb-0.5" style={{ color: '#1FA7A1' }}>
                    Prefer a human?
                  </p>
                  <p className="text-sm sm:text-base font-bold text-slate-900">
                    Talk to VietClinics on WhatsApp
                  </p>
                  <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                    Our coordination team can guide you step-by-step about {treatment.name.toLowerCase()} in Vietnam.
                  </p>
                </div>
                <ArrowRight className="h-5 w-5 text-slate-400 flex-shrink-0" />
              </div>
            </a>
          )}

          <RequestAppointmentForm
            defaultTreatment={treatment.name}
            title={`Request a ${treatment.name} Consultation`}
            subtitle="Speak with VietClinics for guidance based on your treatment goals. Our coordination team will reply with a personalised next step — typically within a few hours."
            testIdPrefix="treatment-req"
          />
        </div>
      </section>

      {/* Related Treatments */}
      {relatedTreatments.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="mb-8">
              <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">You May Also Consider</p>
              <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
                Related Treatments
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedTreatments.map((r) => (
                <Link
                  key={r.slug}
                  to={`/treatments/${r.slug}`}
                  className="group bg-white border border-slate-100 rounded-[20px] overflow-hidden hover:-translate-y-1 transition-all duration-300"
                  style={{ boxShadow: '0 4px 16px rgba(15,42,85,0.04)' }}
                  data-testid={`related-treatment-${r.slug}`}
                >
                  <div className="h-40 overflow-hidden">
                    <img src={r.image} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <p className="text-[10px] font-bold text-[#1FA7A1] uppercase tracking-wider">{r.category}</p>
                    <h3 className="font-extrabold text-slate-900 text-base mt-1 group-hover:text-[#1FA7A1] transition-colors">{r.name}</h3>
                    <p className="text-xs text-slate-500 mt-2">{r.startingPrice}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Medical Disclaimer */}
      <section className="py-10 bg-white border-t border-slate-100">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-slate-50 rounded-2xl border border-slate-100 p-5 flex gap-3">
            <Info className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-slate-500 leading-relaxed">
              <strong className="text-slate-700">Medical disclaimer.</strong>{' '}
              The information on this page is for educational purposes only and does not constitute medical advice.
              Pricing, recovery times and outcomes vary by individual. Always consult a qualified healthcare
              professional before undertaking any treatment. VietClinics is a concierge platform — we connect
              patients with independent, verified healthcare providers and do not provide medical services directly.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const FaqItem = ({ item, index }) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="bg-slate-50 rounded-[20px] border border-slate-100 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
        data-testid={`faq-toggle-${index}`}
      >
        <span className="font-bold text-slate-900 text-sm sm:text-base">{item.q}</span>
        <ChevronDown
          className={`h-5 w-5 text-slate-400 transition-transform duration-200 flex-shrink-0 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-5 -mt-1 text-slate-600 text-sm leading-relaxed">
          {item.a}
        </div>
      )}
    </div>
  );
};

export default TreatmentDetailPage;
